/**
 * The "what runs here" record (YAG-849, spec 2026-10-08 §3): one row per
 * workspace repository saying what resolved, what is on PATH, how the setup
 * commands and checks last ran, which registry credentials it needs and
 * whether they are set, and what is missing. Written by the setup run, every
 * Wright build and the bench; read by Reeve's planning block, Wright's check
 * probe, the draft PR's Environment section, the repository page and
 * `sandboxCommands`. Mirrors the `repo_capabilities` row in camelCase.
 *
 * Every sentence here is composed from the record's own codes and names,
 * never from command output.
 */
import { z } from "zod";
import { resolvedToolSchema } from "./toolchainPlanTypes.js";
export const CAPABILITY_SOURCES = ["setup_run", "build", "bench"];
/** The most tickets one recorded check names (YAG-1133). */
export const MAX_CHECK_TICKETS = 20;
export const CAPABILITY_CHECK_STATUSES = ["green", "red", "could_not_run"];
export const CAPABILITY_DEPENDENCY_STATUSES = ["installed", "failed", "skipped"];
export const CAPABILITY_MISSING_KINDS = [
    "tool",
    "credential",
    "service",
    "origin",
    "system_package",
];
/**
 * Why something is missing. Tool causes are the toolchain plan's own
 * (`UnsatisfiableReason`, `ToolchainInstallCause`) plus `not_installed` (the
 * probe found no such command); credential causes say whether the variable is
 * unset, the host refused it, or the GitHub App cannot read the owner.
 */
export const CAPABILITY_MISSING_CAUSES = [
    "not_installed",
    "backend_not_allowed",
    "below_floor",
    "not_baked",
    "install_too_slow",
    "unverifiable",
    "install_timed_out",
    "checksum_mismatch",
    "toolchain_install_failed",
    "variable_not_set",
    "refused",
    "app_not_installed",
    "not_provided",
    "unreachable",
];
/** The package ecosystems a registry credential is wired for. */
export const REGISTRY_ECOSYSTEMS = [
    "npm",
    "composer",
    "nuget",
    "go",
    "pip",
    "rubygems",
    "ghcr",
];
/** A record older than this reads "last seen <date>" on the page and in Reeve's block. */
export const CAPABILITY_RECORD_STALE_DAYS = 14;
const text = (max) => z.string().max(max);
const iso = z.string().max(40);
export const capabilityToolOnPathSchema = z
    .object({ name: text(120), path: text(500), version: text(200).nullable() })
    .strict();
export const capabilityDependencySchema = z
    .object({
    command: text(2000),
    status: z.enum(CAPABILITY_DEPENDENCY_STATUSES),
    exitCode: z.number().int().nullable(),
    cause: text(200).nullable().optional(),
})
    .strict();
export const capabilityCheckSchema = z
    .object({
    command: text(2000),
    status: z.enum(CAPABILITY_CHECK_STATUSES),
    /** A code or one fixed sentence (the probe's cause wording), never output. */
    cause: text(500).nullable().optional(),
    exitCode: z.number().int().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    at: iso,
    /**
     * Where the check came from (YAG-1133): the writer that last ran it, the
     * head it ran on, and the tickets whose builds ran it. Absent on a check
     * recorded before provenance.
     */
    source: z.enum(CAPABILITY_SOURCES).optional(),
    headSha: text(64).nullable().optional(),
    tickets: z.array(text(100)).max(MAX_CHECK_TICKETS).optional(),
})
    .strict();
export const capabilityCredentialSchema = z
    .object({
    host: text(253),
    ecosystem: z.enum(REGISTRY_ECOSYSTEMS),
    /** The workspace variable that unlocks the host. */
    variable: text(100),
    /** Other names the repository's own config reads the same value under. */
    aliases: z.array(text(100)).max(20),
    status: z.enum(["set", "missing"]),
    /** The files the host was read from. */
    seenIn: z.array(text(300)).max(20),
})
    .strict();
export const capabilityMissingSchema = z
    .object({
    kind: z.enum(CAPABILITY_MISSING_KINDS),
    /** A tool, a host, a service, an origin or a package name. */
    name: text(253),
    cause: z.enum(CAPABILITY_MISSING_CAUSES),
    /** A version spec for a tool, the variable for a credential, the check command for an unrunnable tool. */
    detail: text(500).optional(),
})
    .strict();
/** What the repository's Dockerfile and devcontainer say about its environment (YAG-838 input). */
export const capabilityEvidenceSchema = z
    .object({
    /** Dockerfile `FROM` images and the devcontainer `image`. */
    images: z.array(text(300)).max(20),
    /** Packages a Dockerfile `RUN apt-get install` (or `apk add`) installs. */
    systemPackages: z.array(text(120)).max(60),
    /** devcontainer `features` keys. */
    features: z.array(text(300)).max(30),
    /** The devcontainer `postCreateCommand`, when it is a string. */
    postCreateCommand: text(1000).nullable(),
    /** The files the evidence was read from. */
    files: z.array(text(300)).max(10),
})
    .strict();
export const repoCapabilityRecordSchema = z
    .object({
    repoId: z.string().uuid(),
    workspaceId: z.string().uuid(),
    headSha: text(64).nullable(),
    source: z.enum(CAPABILITY_SOURCES),
    toolchain: z.array(resolvedToolSchema).max(40),
    toolLock: text(200_000).nullable(),
    toolsOnPath: z.array(capabilityToolOnPathSchema).max(80),
    dependencies: z.array(capabilityDependencySchema).max(60),
    checks: z.array(capabilityCheckSchema).max(40),
    credentials: z.array(capabilityCredentialSchema).max(40),
    missing: z.array(capabilityMissingSchema).max(60),
    evidence: capabilityEvidenceSchema.nullable(),
    recordedAt: iso,
    runId: text(100).nullable(),
})
    .strict();
/** Whether the record is past the fourteen days after which it reads "last seen". */
export function capabilityRecordStale(record, now = new Date()) {
    const at = Date.parse(record.recordedAt);
    return (Number.isFinite(at) && now.getTime() - at > CAPABILITY_RECORD_STALE_DAYS * 24 * 60 * 60_000);
}
const TOOL_REASONS = {
    backend_not_allowed: "it installs only from a source that does not verify its downloads",
    below_floor: "the version is older than builds install",
    not_baked: "builds carry only the baked PHP lines",
    install_too_slow: "it compiles from source for longer than a build allows",
    unverifiable: "its download left no checksum to prove it",
    install_timed_out: "the toolchain install ran past its eight minutes",
    checksum_mismatch: "its download did not match its publisher's checksum",
    toolchain_install_failed: "it could not be downloaded and installed",
};
/**
 * The fix sentence for one missing entry, as the page's Missing list, Reeve's
 * block and the draft PR's Environment section read it: "Needs a token for
 * nuget.pkg.github.com: set GITHUB_PACKAGES_TOKEN in the repository's
 * Variables, for it alone or for every repository."
 */
export function missingSentence(entry) {
    const detail = entry.detail?.trim();
    switch (entry.kind) {
        case "credential":
            if (entry.cause === "app_not_installed")
                return `The GitHub App is not installed on ${entry.name}, so builds cannot read its repositories.`;
            if (entry.cause === "refused")
                return `${entry.name} refused the build's token (401 or 403)${detail ? `: check that ${detail} can read its packages` : ""}.`;
            return `Needs a token for ${entry.name}: set ${detail || "its variable"} in the repository's Variables, for it alone or for every repository.`;
        case "tool": {
            if (entry.cause === "not_installed")
                return `${entry.name} is not in the build sandbox${detail ? `, so \`${detail}\` runs only after a build installs it` : ""}. Suggested setup step: install ${entry.name}.`;
            const reason = TOOL_REASONS[entry.cause] ?? "it could not be installed";
            return `${entry.name}${detail ? ` ${detail}` : ""} is not available to builds: ${reason}.`;
        }
        case "service":
            return `The build sandbox does not run ${entry.name}, so a check that needs it falls to CI or a person.`;
        case "origin":
            return `${entry.name} cannot be reached from the build sandbox.`;
        case "system_package":
            return `${entry.name} is a system package the build sandbox does not install.`;
    }
}
//# sourceMappingURL=repoCapabilities.js.map